import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import Moment from 'moment';
import { toast } from 'react-toastify';
import {
    get as _get,
    isEqual as _isEqual,
    cloneDeep as _cloneDeep,
} from 'lodash';

import { makeStyles, useTheme } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Input from '@mui/material/Input';
import InputAdornment from '@mui/material/InputAdornment';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ButtonGroup from '@mui/material/ButtonGroup';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import Checkbox from '@mui/material/Checkbox';
import RadioGroup from '@mui/material/RadioGroup';

import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import SettingsIcon from '@mui/icons-material/Settings';

import Validator, { useValidate, checkValidity } from '../../../../components/validator';

import SystemService from '../../../../data/system-service';
import RolesService from '../../../../data/roles-service';
import Auth from '../../../../data/auth-service';
import DataContext from '../../../../data/data-context';

const useStyles = makeStyles(theme => ({
    input: {
        width: '120px',
    },
    formControl: {
        margin: theme.spacing(1),
        width: '100%',
    },
    select: {
        marginTop: -(theme.spacing(2)),
        marginBottom: theme.spacing(1),
        width: '250px',
    },
    chips: {
        display: 'flex',
        flexWrap: 'wrap',
        minHeight: 36,
    },
    chip: {
        margin: 2,
        background: theme.palette.secondary.main,
        color: theme.palette.secondary.contrastText,
        fontSize: 16,
        '& .MuiChip-deleteIcon': {
            color: 'rgba(255, 255, 255, 0.75)',
        },
    },
    roleButton: {
        float: 'right',
        marginLeft: theme.spacing(2),
    },
    hideFileInput: {
        display: 'none',
    },
    ldapLogContainer: {
        display: 'flex',
        alignItems: 'baseline',
        height: '400px',
        overflow: 'scroll',
    },
    ldapLogs: {
        background: theme.styled.paper.background,
        color: theme.palette.text.primary,
        border: `1px solid ${theme.palette.primary.main}`,
        width: '100%',
        padding: theme.spacing(3),
        minHeight: '350px',
    },
    ldapSuccess: {
        background: theme.palette.success.main,
        color: theme.palette.success.contrastText,
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        borderRadius: '5px',
    },
    ldapUser: {
        background: theme.palette.info.main,
        color: theme.palette.info.contrastText,
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        borderRadius: '5px',
    },
    ldapError: {
        background: theme.palette.error.main,
        color: theme.palette.error.contrastText,
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        borderRadius: '5px',
    },
    ldapSuccessMessage: {
        color: theme.palette.success.main,
    },
    ldapUserMessage: {
        color: theme.palette.info.main,
    },
    ldapErrorMessage: {
        color: theme.palette.error.main,
    },
    connectionTimeout: {
        minWidth: 200,
    },
    certAttributes: {
        '& .MuiFormControlLabel-root': {
            marginRight: theme.spacing(2),
        }
    }
}));

const AuthLDAP = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const { systemSettings, updateSystemSettings } = useContext(DataContext);
    const [roles, setRoles] = useState([]);
    const [current, setCurrent] = useState({});
    const [ldapLogs, setLDAPLogs] = useState(null);
    const [disableCheck, setDisableCheck] = useState(false);
    const [changes, setChanges] = useState(false);
    const daForm = useValidate({});
    const externalPerms = Auth.permissions.get('AuthenticationExternal');

    useEffect(() => {
        getRoles();
        getLogs();
    }, []);

    useEffect(() => {
        if (systemSettings && Object.prototype.hasOwnProperty.call(systemSettings, 'auth')) {
            const s = _cloneDeep(systemSettings);

            s.auth.okta.enabled = false;
            s.auth.saml.enabled = false;
            s.auth.ldap.disabled = false;

            s.auth.ldap.explicit_bans = s.auth.ldap.explicit_bans || null;
            s.auth.ldap.role_queries = s.auth.ldap.role_queries || null;

            setCurrent(s);
        }
    }, [systemSettings]);

    useEffect(() => {
        if (!current || !current.auth) return;
        setChanges(!_isEqual(current.auth, systemSettings.auth));
    }, [current, systemSettings]);

    const getRoles = () => {
        RolesService.getRoles().then(res => {
            setRoles(res.data);
        });
    };

    const getLogs = () => {
        SystemService.getLDAPLogs().then(res => {
            setLDAPLogs(res);
        });
    };

    const handleCheckLDAP = () => {
        setDisableCheck(true);
        SystemService.checkLDAPConn(current.auth.ldap).then(res => {
            toast.success('LDAP connection successful');
            setDisableCheck(false);
        }, err => {
            console.log(err);
            toast.error('Connection attempt to LDAP server failed: ' + err.error);
            setDisableCheck(false);
        });
        getLogs();
    };

    const handleBindConnection = (e, validID, valid) => {
        validID && validID && daForm.set({ ...daForm.get(), [validID]: valid });

        const c = { ...current };
        c.auth.ldap.bind_connection = e.target.value;
        setCurrent(c);
    };

    const handleBindEncryption = (enc) => {
        const c = { ...current };
        c.auth.ldap.bind_encryption = enc;
        setCurrent(c);
    };

    const handleCACert = (e) => {
        const c = { ...current };
        if (!c.auth.ldap.tls_cert) c.auth.ldap.tls_cert = {};
        c.auth.ldap.tls_cert.enable = e.currentTarget.checked;
        setCurrent(c);
    };

    const fileHandler = (file) => {
        const reader = new FileReader();
        return new Promise((resolve, reject) => {
            reader.onload = event => resolve(event.target.result);
            reader.onerror = error => reject(error);
            reader.readAsText(file);
        });
    };

    const handleTLSContents = (e) => {
        fileHandler(e.target.files[0]).then(content => {
            SystemService.certAttributes(content).then((res) => {
                const c = { ...current };
                if (!c.auth.ldap.tls_cert) c.auth.ldap.tls_cert = {};
                c.auth.ldap.tls_cert.contents = content;
                c.auth.ldap.tls_cert.file_name = `${res.subject} (${res.enddate})`;
                setCurrent(c);
            }, (err) => {
                console.error(err);
            });
        }).catch(err => console.error(err));
    };

    const handleFIPS = (e) => {
        const c = { ...current };
        c.auth.ldap.tls_fips = e.currentTarget.checked;
        setCurrent(c);
    };

    const handleVerify = (e) => {
        const c = { ...current };
        c.auth.ldap.verify_cert = e.currentTarget.checked;
        setCurrent(c);
    };

    const handleBindDN = (e) => {
        const c = { ...current };
        c.auth.ldap.bind_dn = e.currentTarget.value;
        setCurrent(c);
    };

    const handleBindPassword = (e) => {
        const c = { ...current };
        c.auth.ldap.bind_password = e.currentTarget.value;
        setCurrent(c);
    };

    const handleConnectionTimeout = (e, validID, valid) => {
        validID && daForm.set({ ...daForm.get(), [validID]: valid });

        const c = { ...current };
        c.auth.ldap.connection_timeout = parseInt(e.target.value);
        setCurrent(c);
    };

    const handleBannedUsers = (e) => {
        const c = { ...current };
        c.auth.ldap.explicit_bans = (e.currentTarget.value
            ? e.currentTarget.value.replace(/\s/g, '').split(',')
            : []);
        setCurrent(c);
    };

    const handleUserDN = (e) => {
        const c = { ...current };
        c.auth.ldap.user_dn = e.currentTarget.value;
        setCurrent(c);
    };

    const handleLoginFilter = (e) => {
        const c = { ...current };
        c.auth.ldap.login_filter = e.currentTarget.value;
        setCurrent(c);
    };
    const handleUserName = (e) => {
        const c = { ...current };
        if (!c.auth.ldap.user_attrs) c.auth.ldap.user_attrs = {};
        c.auth.ldap.user_attrs.name = e.currentTarget.value;
        setCurrent(c);
    };

    const handleUserMail = (e) => {
        const c = { ...current };
        if (!c.auth.ldap.user_attrs) c.auth.ldap.user_attrs = {};
        c.auth.ldap.user_attrs.mail = e.currentTarget.value;
        setCurrent(c);
    };

    const handleAddAuthMapping = () => {
        const c = { ...current };
        if (!c.auth.ldap.role_queries) c.auth.ldap.role_queries = [];
        c.auth.ldap.role_queries.push({ query: '', roles: [] });
        setCurrent(c);
    };

    const handleRemoveAuthMapping = (idx) => {
        const c = { ...current };
        c.auth.ldap.role_queries.splice(idx, 1);
        setCurrent(c);
    };

    const handleAuthMappingQuery = (e, idx) => {
        const c = { ...current };
        c.auth.ldap.role_queries[idx].query = e.currentTarget.value;
        setCurrent(c);
    };

    const handleAuthMapping = (e, idx) => {
        const c = { ...current };
        c.auth.ldap.role_queries[idx].roles = e.target.value;
        setCurrent(c);
    };

    const handleRemoveRole = (mapping, role) => {
        const c = { ...current };
        c.auth.ldap.role_queries[mapping].roles.splice(role, 1);
        setCurrent(c);
    };

    const handleCancel = () => {
        setCurrent(_cloneDeep(systemSettings));
        navigate('/system/configuration');
    };

    const handleSave = () => {
        updateSystemSettings({ auth: current.auth });
    };

    const ITEM_HEIGHT = 48;
    const ITEM_PADDING_TOP = 8;
    const MenuProps = {
        PaperProps: {
            style: {
                maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                width: 250,
            },
        },
    };

    const handleRemoveValidator = (validID) => {
        const s = daForm.get();
        delete s[validID];
        daForm.set(s);
    };

    return !_get(current, 'auth.ldap.disabled', true) ? (
        <Grid container spacing={5}>
            <Grid item xs={12}>
                <Typography>Authentication source or sources. Note that admin is always local.</Typography>
                <Typography>Configure the LDAP connection parameters below.</Typography>
                <Accordion>
                    <AccordionSummary><SettingsIcon />Show LDAP connection log</AccordionSummary>
                    <AccordionDetails className={classes.ldapLogContainer}>
                        <Box className={classes.ldapLogs}>
                            {(() => {
                                if (ldapLogs) {
                                    return Object.entries(ldapLogs).map(([k, v]) =>
                                        <LDAPLogs
                                            key={'ldap-log-header' + k}
                                            server={k}
                                            logs={v} />
                                    );
                                }
                                return 'No log entries.';
                            })()}
                        </Box>
                    </AccordionDetails>
                </Accordion>
            </Grid>
            <Grid item xs={6}>
                <Box>
                    <Typography>LDAP Server</Typography>
                    <Validator
                        disabled={!externalPerms.write}
                        allowEmpty={current.auth.ldap.disabled}
                        valueType="host"
                        errorText="Must be valid hostname and port"
                        fullWidth
                        endAdornment={externalPerms.write &&
                            <InputAdornment position="end">
                                <Button color="primary" aria-label="check" onClick={handleCheckLDAP} disabled={disableCheck}>Check</Button>
                            </InputAdornment>
                        }
                        placeholder={'ldap.example.com:389'}
                        value={current.auth.ldap.bind_connection || ''}
                        onChange={handleBindConnection}
                        onRemoval={handleRemoveValidator}
                    />
                    <FormHelperText>LDAP server hostname and port. Leave blank to skip LDAP authentication.</FormHelperText>
                </Box>
                <Box mt={3}>
                    <Typography>Encryption</Typography>
                    <ButtonGroup disabled={!externalPerms.write} color="primary" aria-label="outlined primary button group">
                        <Button variant={(current.auth.ldap.bind_encryption === 'plain' || current.auth.ldap.bind_encryption === '') ? 'contained' : ''} aria-label="none" onClick={() => { handleBindEncryption('plain') }}>None</Button>
                        <Button variant={current.auth.ldap.bind_encryption === 'starttls' ? 'contained' : ''} aria-label="starttls extension" onClick={() => { handleBindEncryption('starttls') }}>StartTLS Extension</Button>
                        <Button variant={current.auth.ldap.bind_encryption === 'ssl' ? 'contained' : ''} aria-label="ssl" onClick={() => { handleBindEncryption('ssl') }}>SSL</Button>
                    </ButtonGroup>
                    <FormHelperText>Connection encryption method.</FormHelperText>
                </Box>
                <Box mt={3}>
                    <RadioGroup row disabled={!externalPerms.write} className={classes.certAttributes}>
                        <FormControlLabel
                            labelPlacement="start"
                            label="Verify"
                            disabled={!externalPerms.write || current.auth.ldap.bind_encryption !== 'starttls'}
                            control={
                                <Checkbox
                                    checked={current.auth.ldap.verify_cert || false}
                                    name=""
                                    onChange={handleVerify}
                                />
                            }
                        />
                        <FormControlLabel
                            labelPlacement="start"
                            label="FIPS"
                            disabled={!externalPerms.write || current.auth.ldap.bind_encryption !== 'ssl'}
                            control={
                                <Checkbox
                                    checked={current.auth.ldap.tls_fips || false}
                                    name=""
                                    onChange={handleFIPS}
                                />
                            }
                        />
                        <FormControlLabel
                            labelPlacement="start"
                            label="CA Cert"
                            disabled={!externalPerms.write || current.auth.ldap.bind_encryption !== 'ssl'}
                            control={
                                <Checkbox
                                    checked={_get(current.auth.ldap, 'tls_cert.enable', false) || false}
                                    name=""
                                    onChange={handleCACert}
                                />
                            }
                        />
                        <Box display="flex" ml={3}>
                            <input
                                disabled={!externalPerms.write || !_get(current, 'auth.ldap.tls_cert.enable', false)}
                                className={classes.hideFileInput}
                                id="contained-button-file"
                                type="file"
                                onChange={handleTLSContents}
                            />
                            {externalPerms.write && <label htmlFor="contained-button-file">
                                <Button
                                    variant="contained"
                                    color="primary"
                                    aria-label="upload cert"
                                    component="span"
                                    disabled={!_get(current, 'auth.ldap.tls_cert.enable', false) || current.auth.ldap.bind_encryption !== 'ssl'}
                                >
                                    Upload Cert
                                </Button>
                            </label>}
                        </Box>
                        <Box display={_get(current, 'auth.ldap.tls_cert.file_name') ? 'flex' : 'none'} ml={1} pt={1}>
                            <Typography><code>{_get(current, 'auth.ldap.tls_cert.file_name', '')}</code></Typography>
                        </Box>
                    </RadioGroup>
                </Box>
                <Box mt={3}>
                    <Typography>Bind DN</Typography>
                    <Input
                        disabled={!externalPerms.write}
                        fullWidth
                        placeholder={'dc=example,dc=com'}
                        value={current.auth.ldap.bind_dn || ''}
                        onChange={handleBindDN}
                    />
                    <FormHelperText>Initial connection bind DN. Example: <code>cn=administrator,cn=users,dc=ldap,dc=example,dc=com</code></FormHelperText>
                </Box>
                <Box mt={3}>
                    <Typography>Bind Password</Typography>
                    <Input
                        disabled={!externalPerms.write}
                        type="password"
                        fullWidth
                        value={current.auth.ldap.bind_password || ''}
                        onChange={handleBindPassword}
                    />
                    <FormHelperText>Password for initial bind. This value is encrypted at rest.</FormHelperText>
                </Box>
                <Box mt={3}>
                    <Typography>Connection Timeout</Typography>
                    <Validator
                        className={classes.connectionTimeout}
                        disabled={!externalPerms.write}
                        valueType="range"
                        type="number"
                        inputProps={{ min: 1, max: 30 }}
                        // className={classes.input}
                        endAdornment={<InputAdornment position="end">Seconds</InputAdornment>}
                        value={current.auth.ldap.connection_timeout || 0}
                        onChange={handleConnectionTimeout}
                        onRemoval={handleRemoveValidator}
                    />
                    <FormHelperText>LDAP server connection timeout in seconds. Valid range is 1-30.</FormHelperText>
                </Box>
            </Grid>
            <Grid item xs={6}>
                <Box>
                    <Typography>Banned Users</Typography>
                    <Input
                        disabled={!externalPerms.write}
                        fullWidth
                        value={current.auth.ldap.explicit_bans || []}
                        onChange={handleBannedUsers}
                    />
                    <FormHelperText>
                        Additional separate list of banned LDAP users. Users can be banned
                        individually after login from the Users page, or they can be banned
                        explicitly (before or after login) here. Type one or more names,
                        separated with a comma.
                    </FormHelperText>
                </Box>
                <Box mt={3}>
                    <Typography>User Base DN</Typography>
                    <Input
                        disabled={!externalPerms.write}
                        fullWidth
                        placeholder={'ou=users,dc=example,dc=com'}
                        value={current.auth.ldap.user_dn || ''}
                        onChange={handleUserDN}
                    />
                    <FormHelperText>Base DN for user authentication. Example: <code>ou=users,dc=example,dc=com</code></FormHelperText>
                </Box>
                <Box mt={3}>
                    <Typography>User Query</Typography>
                    <Input
                        disabled={!externalPerms.write}
                        fullWidth
                        value={current.auth.ldap.login_filter || ''}
                        onChange={handleLoginFilter}
                    />
                    <FormHelperText>
                        User search format string for authentication. Use $username to indicate the user login. Example:
                        <code>(&(objectClass=organizationalPerson)(sAMAccountName=$username))</code>
                    </FormHelperText>
                </Box>
                <Box mt={3}>
                    <Typography>User Attributes</Typography>
                    <Box mt={2}>
                        <TextField
                            disabled={!externalPerms.write}
                            fullWidth
                            label='Name'
                            placeholder='cn, displayName, or similar'
                            value={_get(current, 'auth.ldap.user_attrs.name') || ''}
                            onChange={handleUserName}
                        />
                        <FormHelperText>LDAP attributes mapped to local values.</FormHelperText>
                    </Box>
                    <Box mt={2}>
                        <TextField
                            disabled={!externalPerms.write}
                            fullWidth
                            label='Mail'
                            placeholder='mail, email, or similar'
                            value={_get(current, 'auth.ldap.user_attrs.mail', '')}
                            onChange={handleUserMail}
                        />
                    </Box>
                </Box>
            </Grid>
            <Grid container item xs={12}>
                <Grid item xs={12}>
                    <Box display="inline-flex">
                        <Typography variant="h5">Authorization Mappings</Typography>
                    </Box>
                    {externalPerms.write &&
                        <Button className={classes.roleButton} variant="contained" color="primary" aria-label="add authorization mapping" onClick={handleAddAuthMapping}><AddIcon /> Add</Button>
                    }
                </Grid>
                <Grid item xs={6}>
                    <Typography>Query</Typography>
                </Grid>
                <Grid item xs={6}>
                    <Typography>Roles</Typography>
                </Grid>
            </Grid>
            <Grid container item xs={12} spacing={5}>
                {(current.auth.ldap.role_queries || []).map((v, i) => {
                    return (
                        <React.Fragment key={'ldap-auth-map-' + i}>
                            <Grid item xs={6}>
                                <Input
                                    fullWidth
                                    disabled={!externalPerms.write}
                                    value={current.auth.ldap.role_queries[i].query || ''}
                                    onChange={(e) => { handleAuthMappingQuery(e, i) }}
                                />
                                <FormHelperText>
                                    Authorization or membership query. <b>$username</b> will be replaced with authenticated username.
                                </FormHelperText>
                                <FormHelperText>
                                    Example: <code>(&(sAMAccountName=$username)(memberof=CN=JacksonFive,CN=Users,DC=example,DC=com))</code>
                                </FormHelperText>
                            </Grid>
                            <Grid item xs={6}>
                                <FormControl className={classes.select}>
                                    <InputLabel id={'role-select-label-' + i}>Role</InputLabel>
                                    <Select
                                        disabled={!externalPerms.write}
                                        labelId="role-select-label"
                                        id={'role-select-id-' + i}
                                        value={current.auth.ldap.role_queries[i].roles || []}
                                        multiple
                                        onChange={(e) => { handleAuthMapping(e, i) }}
                                        input={<Input id={'role-select-input-' + i} />}
                                        renderValue={(rolesSelected) => (
                                            <div className={classes.chips}>
                                                {rolesSelected.map((v, j) => {
                                                    return <Chip
                                                        key={'role-chip-' + j}
                                                        label={v}
                                                        className={classes.chip}
                                                        onMouseDown={e => { e.stopPropagation() }}
                                                        onDelete={() => { handleRemoveRole(i, j) }}
                                                    />;
                                                })}
                                            </div>
                                        )}
                                        MenuProps={MenuProps}
                                    >
                                        {roles.map((v, i) => { return <MenuItem key={'role-select-' + i} value={v.name}>{v.name} - {v.description}</MenuItem> })}
                                    </Select>
                                </FormControl>
                                {externalPerms.write &&
                                    <Button className={classes.roleButton} variant="contained" color="secondary" aria-label="remove auth mapping" onClick={() => { handleRemoveAuthMapping(i) }}><RemoveIcon /> Remove</Button>
                                }
                                <FormHelperText>Mapped roles. When the query returns one or more values, the user is granted these roles.</FormHelperText>
                            </Grid>
                        </React.Fragment>
                    );
                })}
            </Grid>
            {externalPerms.write && <Grid item xs={12}>
                <Button
                    className={classes.roleButton}
                    variant="contained"
                    color="primary"
                    aria-label="save"
                    onClick={handleSave}
                    disabled={!changes || !checkValidity(daForm.get())}>
                    Save
                </Button>
                <Button
                    className={classes.roleButton}
                    variant="contained"
                    color="secondary"
                    aria-label="cancel"
                    onClick={handleCancel}
                    // disabled={!changes}
                >
                    Cancel
                </Button>
            </Grid>}
        </Grid>
    ) : null;
};

const LDAPLogs = ({ server, logs }) => {
    return (
        <Box width="100%">
            <Typography>Logs for server {server}</Typography>
            <Box display="flex" width="100%">
                <hr style={{ display: 'flex', flexGrow: 1 }} />
            </Box>
            {(logs || []).map((x, j) => {
                return (
                    <LDAPLog key={j} log={x} />
                );
            })}
        </Box>
    );
};
LDAPLogs.propTypes = {
    logs: PropTypes.array,
    server: PropTypes.string,
};

// React.memo to only render once
const LDAPLog = React.memo(function LDAPLog ({ log }) {
    const theme = useTheme();
    const [type, setType] = useState();

    const hasProperty = (d, k) => Object.prototype.hasOwnProperty.call(d, k);

    useEffect(() => {
        if (hasProperty(log, 'success')) setType('success');
        else if (hasProperty(log, 'user')) setType('user');
        else if (hasProperty(log, 'error')) setType('error');
        else if (hasProperty(log, 'empty')) setType('query');
    }, [log]);

    return (
        <Box display="flex" width="100%">
            <Box px={2} py={1}>
                {Moment(log.timestamp).format(theme.date.longtz)}
            </Box>
            <Box px={2} py={1} display='flex'>
                {{
                    success: <LDAPLogSuccess log={log} />,
                    user: <LDAPLogUser log={log} />,
                    error: <LDAPLogError log={log} />,
                    empty: <LDAPLogQuery log={log} />,
                }[type]}
            </Box>
        </Box>
    );
});
LDAPLog.propTypes = {
    log: PropTypes.object,
    index: PropTypes.number
};

const LDAPLogSuccess = React.memo(function LDAPLog ({ log }) {
    const classes = useStyles();

    return (
        <React.Fragment>
            <Box className={classes.ldapSuccess}>Success</Box>
            <Box ml={1} className={classes.ldapSuccessMessage}>
                {log.success}
            </Box>
        </React.Fragment>
    );
});
LDAPLogSuccess.propTypes = {
    log: PropTypes.object,
    index: PropTypes.number
};

const LDAPLogUser = React.memo(function LDAPLog ({ log }) {
    const classes = useStyles();

    return (
        <React.Fragment>
            <Box className={classes.ldapUser}>User</Box>
            <Box ml={1} className={classes.ldapUserMessage}>
                {log.user ? log.user.name : ''}
            </Box>
        </React.Fragment>
    );
});
LDAPLogUser.propTypes = {
    log: PropTypes.object,
    index: PropTypes.number
};

const LDAPLogError = React.memo(function LDAPLog ({ log }) {
    const classes = useStyles();

    return (
        <React.Fragment>
            <Box className={classes.ldapError}>Error</Box>
            <Box ml={1} className={classes.ldapErrorMessage}>
                {log.error}
            </Box>
        </React.Fragment>
    );
});
LDAPLogError.propTypes = {
    log: PropTypes.object,
    index: PropTypes.number
};

const LDAPLogTLS = React.memo(function LDAPLog ({ log }) {
    const classes = useStyles();

    return (
        <React.Fragment>
            <Box className={classes.ldapUser}>Query</Box>
            <Box ml={1} className={classes.ldapUserMessage}>
                {log.empty}
            </Box>
        </React.Fragment>
    );
});
LDAPLogTLS.propTypes = {
    log: PropTypes.object,
    index: PropTypes.number
};

const LDAPLogQuery = React.memo(function LDAPLog ({ log }) {
    const classes = useStyles();

    return (
        <React.Fragment>
            <Box className={classes.ldapUser}>Query</Box>
            <Box ml={1} className={classes.ldapUserMessage}>
                {log.empty}
            </Box>
        </React.Fragment>
    );
});
LDAPLogQuery.propTypes = {
    log: PropTypes.object,
    index: PropTypes.number
};

export default AuthLDAP;
